import { useEffect, useState } from "react";
import { usePortalScope } from "@/analytics/PortalScope";
import { Button, Dialog } from "@/components/ui";
import { useAuthedApi } from "@/api/useAuthedApi";
import { humanRole } from "./roleLabel";

interface Props {
  /** The member being edited; null keeps the dialog closed. */
  member: { user_id: string; email: string | null } | null;
  currentRoles: string[];
  roleOptions: string[];
  onClose: () => void;
  /** Fired after any write landed — including a partial one before a later
   *  call failed — so the caller re-reads roles rather than trusting ours. */
  onChanged: () => void;
}

export function EditMemberRolesDialog({ member, currentRoles, roleOptions, onClose, onChanged }: Props) {
  const { roles: rolesApi } = useAuthedApi();
  const scope = usePortalScope("members-edit_roles_dialog")["data-ph-scope"];
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!member) return;
    setSelected(new Set(currentRoles));
    setError(null);
    // Reset only when a different member opens — a roles refetch mid-edit
    // must not clobber unsaved ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.user_id]);

  function toggle(role: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  }

  const toGrant = [...selected].filter((r) => !currentRoles.includes(r));
  const toRevoke = currentRoles.filter((r) => !selected.has(r));
  const unchanged = toGrant.length === 0 && toRevoke.length === 0;

  async function handleSave() {
    if (!member) return;
    setError(null);
    setBusy(true);
    let wrote = false;
    try {
      // Grants before revokes, one at a time: a failure part-way then leaves
      // the member with extra access rather than none, and the server's
      // last-admin / self-lockout guards on revoke see the new grants.
      for (const role of toGrant) {
        await rolesApi.grant(member.user_id, role);
        wrote = true;
      }
      for (const role of toRevoke) {
        await rolesApi.revoke(member.user_id, role);
        wrote = true;
      }
      onChanged();
      onClose();
    } catch (e) {
      setError((e as Error).message || "Could not update roles");
      if (wrote) onChanged();
    } finally {
      setBusy(false);
    }
  }

  function handleClose() {
    if (busy) return;
    onClose();
  }

  return (
    <Dialog open={member !== null} onClose={handleClose} title="Edit roles" size="sm" scope={scope}>
      <div className="px-6 py-5 space-y-4">
        <p className="text-[13px] text-ink-muted break-all">{member?.email ?? member?.user_id}</p>
        <fieldset className="space-y-2">
          <legend className="text-[13px] font-medium text-ink mb-1.5">Roles</legend>
          {roleOptions.map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm text-ink">
              <input
                data-ph="toggle_role"
                type="checkbox"
                checked={selected.has(r)}
                disabled={busy}
                onChange={() => toggle(r)}
                className="accent-accent"
              />
              {humanRole(r)}
            </label>
          ))}
        </fieldset>
        {selected.size === 0 && (
          <p className="text-[13px] text-ink-muted">A member needs at least one role.</p>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button data-ph="cancel" variant="secondary" size="sm" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            data-ph="submit"
            size="sm"
            onClick={handleSave}
            disabled={busy || unchanged || selected.size === 0}
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
